import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { t } from 'i18next';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useUsers } from '../../../hooks/useUsers';
import { useGroups } from '../../../hooks/useGroups';
import { assignPlanToGroup, assignPlanToPatient } from '../../../services/plans';
import {
    assignQuestionnaireToGroup,
    assignQuestionnaireToPatients
} from '../../../services/questionnaire';

import {
    assignLearningToGroup,
    assignLearningToPatients
} from '../../../services/aprender';

const getSundayForWeek = (startDate, weekNumber) => {
    const start = dayjs(startDate);

    const firstSunday = start.day() === 0 ? start : start.add(7 - start.day(), 'day');

    return firstSunday.add(weekNumber - 1, 'week').startOf('day');
};

const BulkAssignDialog = ({
    open,
    onClose,
    planUUIDs,
    planDurationWeeks,
    questionnaires = [],
    initialQuestionnaires = [],
    learningContents = [],
    initialLearningContents = []
}) => {
    const [assignMode, setAssignMode] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);

    const [selectedPatientUUIDs, setSelectedPatientUUIDs] = useState([]);
    const [selectedGroupUUIDs, setSelectedGroupUUIDs] = useState([]);
    const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DDTHH:mm'));

    const [assignedTo, setAssignedTo] = useState(null);
    const [assignedEntities, setAssignedEntities] = useState([]);

    const [selectedPatients, setSelectedPatients] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);

    const {
        data: patientRows = [],
        count,
        paginationModel,
        setPaginationModel,
        rowsPerPageOptions
    } = useUsers(['PATIENT']);

    const {
        data: groupRows = [],
        count: groupCount,
        rowsPerPageOptions: groupPageSizeOptions,
        paginationModel: groupPagination,
        setPaginationModel: setGroupPagination
    } = useGroups();

    const handleAssign = async () => {
        setIsAssigning(true);

        const computedEndDate = dayjs(startDate)
            .add(planDurationWeeks, 'week')
            .endOf('day')
            .format('YYYY-MM-DDTHH:mm');

        try {
            if (assignMode === 'patients') {
                for (const patientUUID of selectedPatientUUIDs) {
                    await assignPlanToPatient(
                        planUUIDs,
                        patientUUID,
                        startDate,
                        computedEndDate
                    );
                }
            } else if (assignMode === 'group') {
                for (const gid of selectedGroupUUIDs) {
                    await assignPlanToGroup(planUUIDs, gid, startDate, computedEndDate);
                }
            }

            for (const q of initialQuestionnaires) {
                if (assignMode === 'patients') {
                    await assignQuestionnaireToPatients(
                        q.questionnaireUuid,
                        selectedPatientUUIDs,
                        startDate
                    );
                } else if (assignMode === 'group') {
                    for (const gid of selectedGroupUUIDs) {
                        await assignQuestionnaireToGroup(
                            q.questionnaireUuid,
                            gid,
                            startDate
                        );
                    }
                }
            }

            for (const q of questionnaires) {
                if (!Array.isArray(q.assignedWeeks)) continue;

                for (const week of q.assignedWeeks) {
                    const sunday = getSundayForWeek(startDate, week).format(
                        'YYYY-MM-DDTHH:mm'
                    );

                    if (assignMode === 'patients') {
                        await assignQuestionnaireToPatients(
                            q.questionnaireUuid,
                            selectedPatientUUIDs,
                            sunday
                        );
                    } else if (assignMode === 'group') {
                        for (const gid of selectedGroupUUIDs) {
                            await assignQuestionnaireToGroup(
                                q.questionnaireUuid,
                                gid,
                                sunday
                            );
                        }
                    }
                }
            }

            for (const c of initialLearningContents) {
                if (assignMode === 'patients') {
                    await assignLearningToPatients(
                        c.contentUuid,
                        selectedPatientUUIDs,
                        startDate,
                        null
                    );
                } else if (assignMode === 'group') {
                    for (const gid of selectedGroupUUIDs) {
                        await assignLearningToGroup(c.contentUuid, gid, startDate, null);
                    }
                }
            }

            for (const c of learningContents) {
                if (!c.assignedWeek) continue;

                const sunday = getSundayForWeek(startDate, c.assignedWeek).format(
                    'YYYY-MM-DDTHH:mm'
                );

                if (assignMode === 'patients') {
                    await assignLearningToPatients(
                        c.contentUuid,
                        selectedPatientUUIDs,
                        sunday,
                        null
                    );
                } else if (assignMode === 'group') {
                    for (const gid of selectedGroupUUIDs) {
                        await assignLearningToGroup(c.contentUuid, gid, sunday, null);
                    }
                }
            }

            setAssignedTo(assignMode);
            setAssignedEntities(
                assignMode === 'patients'
                    ? selectedPatients
                    : selectedGroups.map((g) => ({
                          name: t('questionnaires.assigned_group_prefix', {
                              name: g.name
                          })
                      }))
            );

            setAssignMode(null);
            setSelectedPatientUUIDs([]);
            setSelectedGroupUUIDs([]);
            setSelectedPatients([]);
            setSelectedGroups([]);
        } catch (err) {
            console.error('Erro ao atribuir plano + conteúdos:', err);
        } finally {
            setIsAssigning(false);
        }
    };

    const resetDialogState = () => {
        setAssignMode(null);
        setSelectedPatientUUIDs([]);
        setSelectedGroupUUIDs([]);
        setSelectedPatients([]);
        setSelectedGroups([]);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('questionnaires.assign_dialog_title_plan')}</DialogTitle>
            <DialogContent>
                {assignedTo && assignedEntities.length > 0 ? (
                    <Box
                        sx={{
                            backgroundColor: '#e6f4ea',
                            border: '1px solid #4caf50',
                            borderRadius: 2,
                            p: 2,
                            mt: 2
                        }}
                    >
                        <Typography variant="h6" color="green" gutterBottom>
                            {t('questionnaires.assign_success_plan', {
                                type:
                                    assignedTo === 'patients'
                                        ? t('questionnaires.patients')
                                        : t('questionnaires.group')
                            })}
                        </Typography>

                        <ul>
                            {assignedEntities.map((entity, idx) => (
                                <li key={idx}>
                                    <Typography variant="body1">{entity.name}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Box>
                ) : (
                    <>
                        {!assignMode && (
                            <Box display="flex" gap={2}>
                                <Button
                                    variant="contained"
                                    onClick={() => setAssignMode('patients')}
                                >
                                    {t('questionnaires.assign_to_patients')}
                                </Button>

                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => setAssignMode('group')}
                                >
                                    {t('questionnaires.assign_to_group')}
                                </Button>
                            </Box>
                        )}

                        {assignMode === 'patients' && (
                            <>
                                <Grid
                                    container
                                    spacing={2}
                                    alignItems="center"
                                    mb={2}
                                    mt={1}
                                >
                                    <Grid item>
                                        <CalendarMonthIcon color="action" />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            type="date"
                                            label={t('plansDetails.start_date')}
                                            fullWidth
                                            value={dayjs(startDate).format('YYYY-MM-DD')}
                                            onChange={(e) =>
                                                setStartDate(
                                                    dayjs(e.target.value)
                                                        .startOf('day')
                                                        .format('YYYY-MM-DDTHH:mm')
                                                )
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                                <DataGrid
                                    autoHeight
                                    checkboxSelection
                                    disableRowSelectionOnClick
                                    rows={patientRows}
                                    getRowId={(row) => row.role_uuid}
                                    columns={[
                                        {
                                            field: 'name',
                                            headerName: t('questionnaires.columns.name'),
                                            flex: 1
                                        }
                                    ]}
                                    rowSelectionModel={selectedPatientUUIDs.filter((id) =>
                                        patientRows.some((p) => p.role_uuid === id)
                                    )}
                                    onRowSelectionModelChange={(newSelection) => {
                                        setSelectedPatientUUIDs((prevUUIDs) => {
                                            const uuidSet = new Set(prevUUIDs);
                                            newSelection.forEach((id) => uuidSet.add(id));

                                            const currentPageIds = patientRows.map(
                                                (p) => p.role_uuid
                                            );
                                            currentPageIds.forEach((id) => {
                                                if (!newSelection.includes(id))
                                                    uuidSet.delete(id);
                                            });

                                            return Array.from(uuidSet);
                                        });

                                        setSelectedPatients((prev) => {
                                            const map = new Map(
                                                prev.map((p) => [p.uuid, p])
                                            );

                                            newSelection.forEach((id) => {
                                                const row = patientRows.find(
                                                    (p) => p.role_uuid === id
                                                );
                                                if (row)
                                                    map.set(id, {
                                                        uuid: id,
                                                        name: row.name
                                                    });
                                            });

                                            const currentPageIds = patientRows.map(
                                                (p) => p.role_uuid
                                            );
                                            currentPageIds.forEach((id) => {
                                                if (!newSelection.includes(id))
                                                    map.delete(id);
                                            });

                                            return Array.from(map.values());
                                        });
                                    }}
                                    paginationMode="server"
                                    rowCount={count}
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    pageSizeOptions={rowsPerPageOptions}
                                />
                            </>
                        )}

                        {assignMode === 'group' && (
                            <>
                                <Grid
                                    container
                                    spacing={2}
                                    alignItems="center"
                                    mb={2}
                                    mt={1}
                                >
                                    <Grid item>
                                        <CalendarMonthIcon color="action" />
                                    </Grid>
                                    <Grid item xs={12} sm={5}>
                                        <TextField
                                            type="date"
                                            label={t('plansDetails.start_date')}
                                            fullWidth
                                            value={dayjs(startDate).format('YYYY-MM-DD')}
                                            onChange={(e) =>
                                                setStartDate(
                                                    dayjs(e.target.value)
                                                        .startOf('day')
                                                        .format('YYYY-MM-DDTHH:mm')
                                                )
                                            }
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                </Grid>
                                <DataGrid
                                    autoHeight
                                    checkboxSelection
                                    disableRowSelectionOnClick
                                    rows={groupRows}
                                    getRowId={(row) => row.uuid}
                                    columns={[
                                        {
                                            field: 'name',
                                            headerName: t('questionnaires.columns.name'),
                                            flex: 1
                                        },
                                        {
                                            field: 'description',
                                            headerName: t('groups.description_label'),
                                            flex: 2
                                        }
                                    ]}
                                    rowSelectionModel={selectedGroupUUIDs.filter((id) =>
                                        groupRows.some((g) => g.uuid === id)
                                    )}
                                    onRowSelectionModelChange={(newSelection) => {
                                        setSelectedGroupUUIDs((prevUUIDs) => {
                                            const uuidSet = new Set(prevUUIDs);

                                            newSelection.forEach((id) => uuidSet.add(id));

                                            const currentPageIds = groupRows.map(
                                                (g) => g.uuid
                                            );
                                            currentPageIds.forEach((id) => {
                                                if (!newSelection.includes(id))
                                                    uuidSet.delete(id);
                                            });

                                            return Array.from(uuidSet);
                                        });

                                        setSelectedGroups((prev) => {
                                            const map = new Map(
                                                prev.map((g) => [g.uuid, g])
                                            );

                                            newSelection.forEach((id) => {
                                                const row = groupRows.find(
                                                    (g) => g.uuid === id
                                                );
                                                if (row)
                                                    map.set(id, {
                                                        uuid: id,
                                                        name: row.name
                                                    });
                                            });

                                            const currentPageIds = groupRows.map(
                                                (g) => g.uuid
                                            );
                                            currentPageIds.forEach((id) => {
                                                if (!newSelection.includes(id))
                                                    map.delete(id);
                                            });

                                            return Array.from(map.values());
                                        });
                                    }}
                                    paginationMode="server"
                                    rowCount={groupCount}
                                    paginationModel={groupPagination}
                                    onPaginationModelChange={setGroupPagination}
                                    pageSizeOptions={groupPageSizeOptions}
                                />
                            </>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Box>
                    <Button
                        onClick={() => {
                            resetDialogState();
                            onClose();
                        }}
                    >
                        {assignedTo && assignedEntities.length > 0
                            ? t('common.ok')
                            : t('questionnaires.cancel')}
                    </Button>
                </Box>

                <Box display="flex" gap={1}>
                    {assignedTo && (
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setAssignedTo(null);
                                setAssignedEntities([]);
                                setSelectedPatientUUIDs([]);
                                setSelectedGroupUUIDs([]);
                                setAssignMode(null);
                            }}
                        >
                            {t('questionnaires.assign_again')}
                        </Button>
                    )}

                    {assignMode && (
                        <Button
                            variant="contained"
                            onClick={handleAssign}
                            disabled={
                                isAssigning ||
                                (assignMode === 'patients' &&
                                    selectedPatientUUIDs.length === 0) ||
                                (assignMode === 'group' &&
                                    selectedGroupUUIDs.length === 0)
                            }
                        >
                            {isAssigning
                                ? t('plans.assigning')
                                : t('questionnaires.confirm_assignment')}
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default BulkAssignDialog;
